import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

class ProductSectionSwitcher {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #requestSequence = 0;

  constructor() {
    this.handleClick = this.handleClick.bind(this);
    document.addEventListener('click', this.handleClick, true);
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[data-product-switch-link]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    event.preventDefault();
    this.switchSection(link, href);
  }

  /**
   * @param {HTMLElement} link
   * @param {string} href
   */
  async switchSection(link, href) {
    const section = link.closest('.shopify-section');
    if (!section) {
      window.location.href = href;
      return;
    }

    const switcher = link.closest('[data-product-switcher]');
    const hostTemplate = document.querySelector('main[data-template]')?.getAttribute('data-template') || '';
    const isProductPage = hostTemplate === 'product' || hostTemplate.startsWith('product.');
    const shouldUpdateUrl = isProductPage && switcher?.getAttribute('data-update-url') === 'true';

    const sectionId = section.id.replace(/^shopify-section-/, '');
    if (!sectionId) {
      window.location.href = href;
      return;
    }

    const targetUrl = new URL(href, window.location.origin);
    const requestUrl = new URL(targetUrl);
    requestUrl.searchParams.delete('view');
    requestUrl.searchParams.set('section_id', sectionId);

    const requestId = ++this.#requestSequence;
    const selectedMode = section.querySelector('input[data-subscription-mode]:checked')?.getAttribute('value');
    this.#setUpdatingState(section, true);

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(requestUrl.toString(), { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Product section request failed with status ${response.status}`);

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const newProductInformation = doc.querySelector('.product-information');
      const newSection = newProductInformation?.closest('.shopify-section');

      if (!newSection) {
        window.location.href = href;
        return;
      }

      morph(section, newSection);

      if (selectedMode) {
        const modeInput = section.querySelector(`input[data-subscription-mode][value="${selectedMode}"]`);
        if (modeInput instanceof HTMLInputElement) modeInput.checked = true;
      }

      // Notify any listeners that the product section was switched
      section.dispatchEvent(new CustomEvent('product:switched', { bubbles: true }));

      if (isProductPage) {
        const pageTitle = newProductInformation.getAttribute('data-page-title');
        if (pageTitle) {
          const suffixIndex = document.title.lastIndexOf(' | ');
          const suffix = suffixIndex >= 0 ? document.title.slice(suffixIndex) : '';
          document.title = `${pageTitle}${suffix}`;
        }
      }

      if (shouldUpdateUrl && targetUrl.toString() !== window.location.href) {
        requestYieldCallback(() => {
          history.replaceState({}, '', targetUrl.toString());
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      console.error(error);
      window.location.href = href;
    } finally {
      if (requestId !== this.#requestSequence) return;
      requestAnimationFrame(() => this.#setUpdatingState(section, false));
    }
  }

  /**
   * @param {Element} section
   * @param {boolean} isUpdating
   */
  #setUpdatingState(section, isUpdating) {
    if (!(section instanceof HTMLElement)) return;
    section.classList.toggle('variant-updating', isUpdating);

    const productInformation = section.querySelector('.product-information');
    if (productInformation instanceof HTMLElement) {
      productInformation.classList.toggle('variant-updating', isUpdating);
    }
  }
}

new ProductSectionSwitcher();
